import React, { useCallback } from 'react';

import { getZonedDate } from '@eika-infrastruktur/date';

import useAccountChange from '../../../fetchHooks/useAccountChange';
import { useNextBankingDayDispatch } from '../../../fetchHooks/useBankingDays';
import { usePaymentAccountContext } from '../../../hooks/useAccountState';
import usePaymentAccount from '../../../hooks/usePaymentAccount';
import useShowPaymentResult from '../../../hooks/useShowPaymentResult';
import useTranslation from '../../../hooks/useTranslation';
import { SigningContextProvider, useSigningContext } from '../../../signingContext/signingContext';
import { Actions } from '../../../signingContext/signingTypes';
import { EikaModal } from '../../EikaModal';
import CreateRecurringPaymentForm, { ANALYTICS_CATEGORY } from './CreateRecurringPaymentForm';
import { RecurringPaymentActionType } from './form/actions';
import recurringPaymentReducer from './form/reducer';
import { recurringPaymentInitialState } from './form/state';
import useFormContext, { RecurringPaymentFormContext } from './form/useFormContext';
import validate from './form/validate';

import '../RecurringTransfer/CreateRecurringTransferForm.scss';

interface Props {
    show: boolean;
    setShow: React.Dispatch<boolean>;
    refresh: () => void;
}

const today = getZonedDate();

const CreateRecurringPayment: React.FC<Props> = ({ show, setShow, refresh }) => {
    const translated = useTranslation();

    const { signingStatus, signingError, signingOperationPending, showSigningFlow, dispatch } = useSigningContext();
    const { handleChange } = useFormContext();

    const account = usePaymentAccount();
    useNextBankingDayDispatch(today, handleChange, RecurringPaymentActionType.FIRST_BANKING_DATE);

    const refreshAccounts = useAccountChange();
    const { targetFromAccount } = usePaymentAccountContext();

    const updateOnChange = useCallback(() => {
        const hasChanged = !!signingError || !!signingStatus || signingOperationPending;
        if (hasChanged && targetFromAccount?.accountId) {
            refreshAccounts([targetFromAccount?.accountId]);
            refresh();
        }
    }, [signingError, signingStatus, signingOperationPending, targetFromAccount?.accountId, refreshAccounts, refresh]);

    const onCloseRecurringPayment = useCallback(() => {
        updateOnChange();
        handleChange({
            type: RecurringPaymentActionType.RESET,
        });
        dispatch({ type: Actions.RESET });
    }, [dispatch, handleChange, updateOnChange]);

    const showPaymentResult = useShowPaymentResult();
    const hideCloseButton = showSigningFlow && !showPaymentResult;

    return (
        <EikaModal
            id="create-new-recurring-payment"
            open={show}
            setOpen={setShow}
            onClose={onCloseRecurringPayment}
            className="create-new-recurring-payment__modal"
            hideCloseButton={hideCloseButton}
            ariaLabel={translated.recurringPayments.recurringPayment}
            timeOut={signingStatus && signingStatus === 'COMPLETED' ? 2500 : undefined}
            contentShouldStretch
            size="wide"
            desktopMinHeight="small"
        >
            <CreateRecurringPaymentForm onClose={onCloseRecurringPayment} account={account ?? undefined} />
        </EikaModal>
    );
};

export default (props: Props) => (
    <SigningContextProvider analyticsCategory={ANALYTICS_CATEGORY}>
        <RecurringPaymentFormContext
            validateHook={validate}
            reducer={recurringPaymentReducer}
            initialState={recurringPaymentInitialState}
        >
            <CreateRecurringPayment {...props} />
        </RecurringPaymentFormContext>
    </SigningContextProvider>
);
